import { type CSSProperties } from 'react';
import { type Column, type Table } from '@tanstack/react-table';

// Single source of truth for the fixed-layout column-width behavior shared by
// every data table (gallery list, manage tables, drag rows). Previously each
// renderer inlined these rules, so a fix had to be repeated per table and
// silently drifted (e.g. the drag overlay kept the old `=== 150 ? auto`). (DM-2393)

/**
 * Classes for the <Table> element. `table-fixed` locks column widths to the
 * header sizes (so `truncate` clips instead of the row growing to content);
 * `--table-width` (sum of column sizes) is a pixel floor and `min-w-full`
 * stretches the table to fill the container above that floor. Below it the
 * container scrolls instead of squishing columns.
 */
export const fixedTableClassName = 'w-(--table-width)! min-w-full table-fixed';

/** Inline style carrying the pixel-width floor for {@link fixedTableClassName}. */
export const fixedTableStyle = <T>(table: Table<T>): CSSProperties =>
  ({ '--table-width': `${table.getTotalSize()}px` }) as CSSProperties;

/**
 * Width style for a header or body cell. flexWidth columns get a min-width so
 * table-fixed hands them the leftover space; all others get a fixed width.
 */
export const columnWidthStyle = <T>(column: Pick<Column<T>, 'columnDef' | 'getSize'>): CSSProperties =>
  column.columnDef.meta?.flexWidth ? { minWidth: column.getSize() } : { width: column.getSize() };
